import { useEffect, useRef, useState } from 'react';

/**
 * Tracks whether a form is "dirty" relative to its initial value and
 * installs a `beforeunload` guard so accidental tab-close / hard reload
 * surfaces a "you have unsaved changes" confirm.
 *
 * The dirty flag is exposed so callers can render badges / disable save buttons.
 */
export function useDirtyState<T>(current: T, initial: T): boolean {
  const initialRef = useRef(initial);
  // Sync initial when the underlying record id changes (e.g. switching between models).
  useEffect(() => { initialRef.current = initial; }, [initial]);

  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDirty(!shallowEqual(current, initialRef.current));
  }, [current]);

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  return dirty;
}

function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) {
    return false;
  }
  const ak = Object.keys(a as object);
  const bk = Object.keys(b as object);
  if (ak.length !== bk.length) return false;
  for (const k of ak) {
    const av = (a as Record<string, unknown>)[k];
    const bv = (b as Record<string, unknown>)[k];
    if (Array.isArray(av) && Array.isArray(bv)) {
      if (av.length !== bv.length) return false;
      for (let i = 0; i < av.length; i++) {
        if (!Object.is(av[i], bv[i])) return false;
      }
      continue;
    }
    if (!Object.is(av, bv)) return false;
  }
  return true;
}
